<script setup lang="ts">
import { BsError, BsSelect } from '@afo/bsf-core'
import { useCompanyRoles } from '@/domains/partners/multisport/composables/useCompanyRoles'
import { usePhonePrefixes } from '@/domains/partners/multisport/composables/usePhonePrefixes'
import { useMultisportPartnersStore } from '@/domains/partners/multisport/store/useMultiSportPartnersStore'
import TextInput from '@/components/Inputs/TextInput/TextInput.vue'

const { t } = useI18n()
const { form, $v } = useMultisportPartnersStore()
const companyRoles = useCompanyRoles()
const phonePrefixes = usePhonePrefixes()
</script>

<template>
  <div class="stepOne">
    <h1 class="stepOne__title">
      {{ $t('partners.multisport.steps.step1.subTitle') }}
    </h1>

    <div class="stepOne__form">
      <div class="stepOne__fullName">
        <div class="stepOne__half">
          <TextInput
            id="first_name"
            v-model="form.firstName"
            :label="`${t('form.name')} *`"
            :error-message="$v.firstName.$errors[0]?.$message"
            :error="!!$v.firstName.$errors[0]?.$message"
            :required="true"
            @blur="$v.firstName.$touch()"
          />
        </div>

        <div class="stepOne__half">
          <TextInput
            id="last_name"
            v-model="form.lastName"
            name="lastName"
            :label="`${t('form.surname')} *`"
            :error-message="$v.lastName.$errors[0]?.$message"
            :error="!!$v.lastName.$errors[0]?.$message"
            :required="true"
            @blur="$v.lastName.$touch()"
          />
        </div>
      </div>

      <div>
        <BsError :message="$v.companyRole.$errors[0]?.$message">
          <BsSelect
            v-model="form.companyRole"
            :label="`${t('form.companyRole.name')} *`"
            :placeholder="$t('form.companyRole.placeholder')"
            :options="companyRoles"
            :error="!!$v.companyRole.$errors[0]?.$message"
          />
        </BsError>
      </div>

      <div>
        <TextInput
          id="email"
          v-model="form.email"
          name="email"
          :label="`${t('form.email')} *`"
          :error-message="$v.email.$errors[0]?.$message"
          :error="!!$v.email.$errors[0]?.$message"
          :disabled="false"
          :required="true"
          @blur="$v.email.$touch()"
        />
      </div>

      <div class="stepOne__phones">
        <div class="stepOne__half">
          <BsError :message="$v.phonePrefix.$errors[0]?.$message">
            <BsSelect
              v-model="form.phonePrefix"
              :error="!!$v.phonePrefix.$errors[0]?.$message"
              :label="`${t('form.phonePrefix.name')} *`"
              :placeholder="$t('form.phonePrefix.placeholder')"
              :options="phonePrefixes"
            />
          </BsError>
        </div>

        <div class="stepOne__half">
          <TextInput
            id="phone"
            v-model="form.phone"
            type="tel"
            name="phone"
            :label="`${t('form.phoneWithoutPrefix')} *`"
            :error-message="$v.phone.$errors[0]?.$message"
            :error="!!$v.phone.$errors[0]?.$message"
            :disabled="form.phonePrefix ? false : true"
            :required="true"
            @blur="$v.phone.$touch()"
          />
        </div>
      </div>
    </div>

    <div class="stepOne__requiredFields">
      {{ $t('partners.multisport.requiredFields') }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.stepOne {
  &__title {
    @include h11;
    margin-bottom: 3.2rem;
    color: $text-500;
    text-align: center;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 1.6rem;
    padding-bottom: 1rem;
  }

  &__half {
    width: 100%;
  }

  &__fullName {
    display: flex;
    flex-grow: 1;
    flex-direction: row;
    gap: 1.6rem;
    width: 100%;

    :deep(.bs-error) {
      flex: 1 1 0;
      min-width: 0;
    }

    :deep(.bs-input),
    :deep(.bs-select) {
      width: 100%;
    }
  }

  &__phones {
    display: flex;
    flex-direction: column;
    gap: 1.6rem;

    &__phonePrefix {
      flex: 1;
    }

    &__phone {
      flex: 2;
    }
  }

  &__requiredFields {
    @include body-4;
    padding: 3.2rem 0;
    color: $text-200;
    text-align: center;
  }

  @include atL {
    &__half {
      flex: 1;
      width: 50%;
    }

    &__phones {
      flex-direction: row;
      gap: 1.6rem;

      :deep(.bs-error) {
        flex: 1 1 0;
        min-width: 0;
      }

      :deep(.bs-input),
      :deep(.bs-select) {
        width: 100%;
      }
    }

    &__requiredFields {
      text-align: left;
    }
  }
}
</style>

<style lang="scss">
.stepOne, .stepTwo, .stepThree {
  .bs-input__label, .bs-select__label {
    font-size: 1.4rem !important;
  }

  .bs-checkbox__label {
    @include body-3-medium;
    color: $text-300;
  }
}
</style>
